import VanillaLazyLoad from 'vanilla-lazyload';
import readyState from 'ready-state';
import Logger from '@slickdeals/durango/dist/javascript/utilities/logger';
/** If no image fallback is supplied, blank.gif will be used */
const defaultImageFallback = '/images/blank.gif';
const defaultOptions = {
    defaultImageFallback,
    debug: false,
};
export default class LazyLoad {
    constructor(options = defaultOptions) {
        this.options = {};
        this.options.defaultImageFallback = options.defaultImageFallback || defaultOptions.defaultImageFallback;
        this.options.debug = options.debug || defaultOptions.debug;
        readyState.domready.then(this.init.bind(this));
    }
    /**
     * Kicks off lazyLoad functionality and creates a mutationObserver to watch the DOM
     */
    init() {
        // Calling update on the lazy load instance will scan the DOM for images to lazy load
        this.getLazyLoad().update();
    }
    /**
     * Returns a defined, or a new lazy load instance
    */
    getLazyLoad() {
        // If lazyload instance is already created, return instance
        if (this.lazyLoad) {
            return this.lazyLoad;
        }
        // Creates a new instance if there isn't already one
        this.createLazyLoadInstance();
        return this.lazyLoad;
    }
    /**
     * Sets the mutation observer to observe dom changes
     * @param {HTMLElement} element The HTMLElement to observe on
     */
    setObserver(element) {
        // Defines a mutationObserver which will fire when new elements get added onto the page
        if (typeof this.mutationObserver === 'undefined') {
            this.mutationObserver = new window.MutationObserver(mutationsList => this.checkForDomUpdate(mutationsList));
        }
        this.mutationObserver.observe(element, {
            childList: true,
            subtree: true,
        });
    }
    /**
     * Creates a new LazyLoad instance for this.lazyLoad
     */
    createLazyLoadInstance() {
        // Typed as object here because the lazy load lib does not expose the options interface
        let options = {
            data_src: 'lazy-src',
            data_srcset: 'lazy-srcset',
            data_sizes: 'lazy-sizes',
            callback_enter: this.handleEmptyImageSrc.bind(this),
            callback_error: this.setFallbackImage.bind(this),
        };
        // Will output console logs of what the lazy load instance is perceiving / processing the images
        if (this.options.debug) {
            options = {
                ...options,
                callback_enter: (e) => {
                    this.handleEmptyImageSrc(e);
                    Logger.log('🔑 ENTERED', e.getAttribute('alt'));
                },
                callback_loaded: (e) => Logger.log('👍 LOADED', e.getAttribute('alt')),
                callback_error: (e) => {
                    this.setFallbackImage(e);
                    Logger.log('💀 FAILED - Using Fallback', e.getAttribute('alt'));
                },
                callback_finish: () => Logger.log('✔️ FINISHED', document.documentElement.getAttribute('alt')),
            };
        }
        this.lazyLoad = new VanillaLazyLoad(options);
    }
    /**
     * Determine if content has been added to the DOM in order to lazy load it
     * @param mutationsList Array of Mutations that took place on the DOM
     */
    checkForDomUpdate(mutationsList) {
        // Loops through the found mutations
        Object.values(mutationsList).forEach(mutation => {
            // Ensures that the mutations only happened on the childList
            if (mutation.type === 'childList') {
                // Update the lazyLoad instance
                this.getLazyLoad().update();
            }
        });
    }
    /**
     * Check if the src attribute of the image is empty or set to the current url
     * Being set to the current url is also representative of the image having an empty src attribute
     * @param element The image element being lazy loaded
     */
    handleEmptyImageSrc(element) {
        if (element.src === '' || element.src === window.location.href) {
            this.setFallbackImage(element);
        }
    }
    /**
     * Change the src of the image to the fallback specified by the instance
     * @param element The image element being lazy loaded
     */
    setFallbackImage(element) {
        // If fallback data attributes exist on element, use fallback image
        if ('lazyFallback' in element.dataset) {
            // Explicitly using set here to not mutate the argument directly
            element.setAttribute('src', element.dataset.lazyFallback);
            return;
        }
        // If no fallback image is specified for this element, fallback to either default fallback image
        // or the defaultFallback image that was provided to the lazyLoad instance
        element.setAttribute('src', this.options.defaultImageFallback);
    }
}
